import React, { useEffect, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  Button,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  TextField,
  InputAdornment,
  TableSortLabel,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  IconButton,
  Tooltip,
  Checkbox,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import Layout from '../components/Layout';
import { useFilter } from '../contexts/FilterContext';
import { getRefreshPaperStyles } from '../utils/refreshAnimation';
import { alertService } from '../services/alertService';
import { formatDateToTR, formatTimeAgoTR } from '../utils/dateUtils';
import type { Alert as AlertDataType, AlertType, AlertSeverity, AlertStatus } from '../types/alert';
import { AlertType as AlertTypeEnum, AlertSeverity as AlertSeverityEnum, AlertStatus as AlertStatusEnum } from '../types/alert';

const Alerts = () => {
  const { selectedTenantIds, selectedServerIds, refreshTrigger, autoRefresh, isRefreshing } = useFilter();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<AlertDataType[]>([]);
  
  // Paging state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  
  // URL parametresinden status filtresini oku
  const statusParam = searchParams.get('status');
  const getInitialStatus = (): 'all' | AlertStatus | 'unresolved' => {
    if (statusParam === '1' || statusParam === 'new') {
      return AlertStatusEnum.New;
    } else if (statusParam === 'unresolved') {
      return 'unresolved';
    }
    return AlertStatusEnum.New;
  };
  
  const [filter, setFilter] = useState<{
    search: string;
    alertTypes: AlertType[]; // Multiple selection için array
    severity: 'all' | AlertSeverity;
    status: 'all' | AlertStatus | 'unresolved';
  }>({
    search: '',
    alertTypes: [],
    severity: 'all',
    status: getInitialStatus(),
  });
  
  // URL parametrelerinden filtreleri oku
  useEffect(() => {
    const statusParam = searchParams.get('status');
    if (statusParam === '1' || statusParam === 'new') {
      setFilter(prev => ({ ...prev, status: AlertStatusEnum.New }));
    } else if (statusParam === 'unresolved') {
      setFilter(prev => ({ ...prev, status: 'unresolved' }));
    } else if (statusParam === 'all') {
      setFilter(prev => ({ ...prev, status: 'all' }));
    } else if (!statusParam) {
      setFilter(prev => ({ ...prev, status: AlertStatusEnum.New }));
    }

    // alertTypes parametresi (multiple, comma-separated)
    const alertTypesParam = searchParams.get('alertTypes');
    if (alertTypesParam) {
      const types = alertTypesParam.split(',').map(t => parseInt(t, 10) as AlertType).filter(t => !isNaN(t));
      setFilter(prev => ({ ...prev, alertTypes: types }));
      setPage(0); // Filtre değiştiğinde sayfayı sıfırla
    } else {
      // URL'de alertTypes yoksa, filter'ı temizle (eğer önceden set edilmişse)
      setFilter(prev => {
        if (prev.alertTypes.length > 0) {
          return { ...prev, alertTypes: [] };
        }
        return prev;
      });
    }
  }, [searchParams]);
  const [sort, setSort] = useState<{
    field: 'createdAt' | 'severity' | 'status';
    direction: 'asc' | 'desc';
  }>({
    field: 'createdAt',
    direction: 'desc',
  });

  // Resolve dialog state
  const [resolveDialogOpen, setResolveDialogOpen] = useState(false);
  const [selectedAlertForResolve, setSelectedAlertForResolve] = useState<AlertDataType | null>(null);
  const [resolutionNotes, setResolutionNotes] = useState('');
  
  // Resolution notes view dialog state
  const [viewNotesDialogOpen, setViewNotesDialogOpen] = useState(false);
  const [selectedAlertForViewNotes, setSelectedAlertForViewNotes] = useState<AlertDataType | null>(null);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const tenantId = selectedTenantIds.length === 1 ? selectedTenantIds[0] : undefined;
      const serverId = selectedServerIds.length === 1 ? selectedServerIds[0] : undefined;

      // Unresolved durumu için status != Resolved filtrelemesi yap
      const statusFilter = filter.status === 'unresolved' 
        ? undefined // Backend'de status != Resolved için özel bir parametre yok, frontend'de filtreleyeceğiz
        : filter.status !== 'all' 
        ? filter.status 
        : undefined;

      // AlertType filtreleme: Backend'de multiple selection destekleniyor
      const result = await alertService.getAll({
        tenantId,
        serverId,
        alertTypes: filter.alertTypes.length > 0 ? filter.alertTypes : undefined, // Multiple selection backend'de
        severity: filter.severity !== 'all' ? filter.severity : undefined,
        status: statusFilter,
        page: page + 1,
        pageSize,
      });
      
      // Frontend'de sadece unresolved ve arama filtresi uygula (backend'de yapılamayanlar)
      let filteredAlerts = result.items;
      
      // Unresolved durumu için filtrele (backend'de status != Resolved desteği yok)
      if (filter.status === 'unresolved') {
        filteredAlerts = filteredAlerts.filter(a => a.status !== AlertStatusEnum.Resolved);
      }

      // Arama filtresi uygula (backend'de full-text search yok)
      if (filter.search) {
        filteredAlerts = filteredAlerts.filter(a => 
          a.message.toLowerCase().includes(filter.search.toLowerCase()) ||
          a.serverName?.toLowerCase().includes(filter.search.toLowerCase()) ||
          a.applicationName?.toLowerCase().includes(filter.search.toLowerCase()) ||
          a.tenantName.toLowerCase().includes(filter.search.toLowerCase())
        );
      }

      // Sıralama uygula (backend'de sadece CreatedAt desc var, diğerleri frontend'de)
      if (sort.field !== 'createdAt' || sort.direction !== 'desc') {
        filteredAlerts.sort((a, b) => {
          switch (sort.field) {
            case 'createdAt':
              const timeA = new Date(a.createdAt).getTime();
              const timeB = new Date(b.createdAt).getTime();
              return sort.direction === 'asc' ? timeA - timeB : timeB - timeA;
            case 'severity':
              return sort.direction === 'asc'
                ? a.severity - b.severity
                : b.severity - a.severity;
            case 'status':
              return sort.direction === 'asc'
                ? a.status - b.status
                : b.status - a.status;
            default:
              return 0;
          }
        });
      }

      setAlerts(filteredAlerts);
      // Unresolved veya arama filtresi varsa frontend'de filtreleme yapıldı, totalCount'u güncelle
      if (filter.status === 'unresolved' || filter.search) {
        setTotalCount(filteredAlerts.length);
      } else {
        setTotalCount(result.totalCount);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Hatalar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  }, [selectedTenantIds, selectedServerIds, filter.alertTypes, filter.severity, filter.status, filter.search, sort, page, pageSize]);

  useEffect(() => {
    loadData();
  }, [loadData, refreshTrigger]);

  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      loadData();
    }, 60000); // 60 saniye

    return () => clearInterval(interval);
  }, [autoRefresh, loadData]);

  const handleResolveClick = (alert: AlertDataType) => {
    setSelectedAlertForResolve(alert);
    setResolutionNotes('');
    setResolveDialogOpen(true);
  };

  const handleResolveConfirm = async () => {
    if (!selectedAlertForResolve) return;

    try {
      await alertService.resolve(selectedAlertForResolve.id, resolutionNotes || undefined);
      setResolveDialogOpen(false);
      setSelectedAlertForResolve(null);
      setResolutionNotes('');
      await loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Alert çözülürken bir hata oluştu');
    }
  };

  const handleResolveCancel = () => {
    setResolveDialogOpen(false);
    setSelectedAlertForResolve(null);
    setResolutionNotes('');
  };

  const handleSort = (field: 'createdAt' | 'severity' | 'status') => {
    setSort((prev) => ({
      field,
      direction: prev.field === field && prev.direction === 'asc' ? 'desc' : 'asc',
    }));
    setPage(0); // Sıralama değiştiğinde sayfayı sıfırla
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangePageSize = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPageSize(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Filtreleme ve sıralama artık loadData içinde yapılıyor
  const filteredAlerts = alerts; // Direkt alerts kullan (zaten loadData'da filtrelenmiş ve sıralanmış)

  const getSeverityColor = (severity: AlertSeverity): 'error' | 'warning' | 'info' | 'success' => {
    switch (severity) {
      case AlertSeverityEnum.Critical:
        return 'error';
      case AlertSeverityEnum.High:
        return 'error';
      case AlertSeverityEnum.Medium:
        return 'warning';
      case AlertSeverityEnum.Low:
        return 'info';
      default:
        return 'info';
    }
  };

  const getStatusColor = (status: AlertStatus): 'error' | 'warning' | 'success' => {
    switch (status) {
      case AlertStatusEnum.New:
        return 'error';
      case AlertStatusEnum.Resolved:
        return 'success';
      default:
        return 'warning';
    }
  };

  const getStatusIcon = (status: AlertStatus): React.ReactElement | undefined => {
    switch (status) {
      case AlertStatusEnum.New:
        return <ErrorIcon sx={{ fontSize: 16 }} />;
      case AlertStatusEnum.Resolved:
        return <CheckCircleIcon sx={{ fontSize: 16 }} />;
      default:
        return undefined;
    }
  };

  // formatTimeAgo artık dateUtils'ten geliyor (formatTimeAgoTR)

  if (loading && alerts.length === 0) {
    return (
      <Layout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout>
      <Box
        sx={{
          position: 'relative',
          opacity: isRefreshing ? 0.7 : 1,
          transition: 'opacity 0.3s ease-in-out',
          pointerEvents: isRefreshing ? 'none' : 'auto',
        }}
      >
      <Box mb={4}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            mb: 1,
          }}
        >
          Hatalar
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Sistem genelindeki tüm alert'leri görüntüleyin ve yönetin
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Filtreler */}
      <Paper
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          ...getRefreshPaperStyles(isRefreshing),
        }}
      >
        <Box display="flex" gap={2} flexWrap="wrap">
          <TextField
            size="small"
            placeholder="Ara..."
            value={filter.search}
            onChange={(e) => {
              setFilter((prev) => ({ ...prev, search: e.target.value }));
              setPage(0); // Arama değiştiğinde sayfayı sıfırla
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            }}
            sx={{ flex: { xs: '1 1 100%', sm: '1 1 300px' } }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Tip</InputLabel>
            <Select
              multiple
              value={filter.alertTypes}
              label="Tip"
              onChange={(e) => {
                const value = e.target.value;
                const selectedTypes = typeof value === 'string' ? [value] : value;
                setFilter((prev) => ({ ...prev, alertTypes: selectedTypes as AlertType[] }));
                setPage(0); // Filtre değiştiğinde sayfayı sıfırla
              }}
              renderValue={(selected) => {
                if (selected.length === 0) {
                  return 'Tümü';
                }
                if (selected.length === 1) {
                  const typeNames: Record<AlertType, string> = {
                    [AlertTypeEnum.AgentHeartbeat]: 'Agent Heartbeat',
                    [AlertTypeEnum.ApplicationUnhealthy]: 'Uygulama Sağlıksız',
                    [AlertTypeEnum.DiskUsage]: 'Disk Kullanımı',
                    [AlertTypeEnum.TaskNotRun]: 'Görev Çalışmadı',
                    [AlertTypeEnum.CpuUsage]: 'CPU Kullanımı',
                    [AlertTypeEnum.RamUsage]: 'RAM Kullanımı',
                    [AlertTypeEnum.EStatement]: 'eStatement',
                  };
                  return typeNames[selected[0] as AlertType] || 'Bilinmeyen';
                }
                return `${selected.length} tip seçildi`;
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 320,
                    overflow: 'hidden',
                  },
                },
                MenuListProps: {
                  sx: {
                    maxHeight: 320,
                    overflowY: 'auto',
                  },
                },
              }}
            >
              <MenuItem value={AlertTypeEnum.AgentHeartbeat}>
                <Checkbox checked={filter.alertTypes.indexOf(AlertTypeEnum.AgentHeartbeat) > -1} />
                <ListItemText primary="Agent Heartbeat" />
              </MenuItem>
              <MenuItem value={AlertTypeEnum.ApplicationUnhealthy}>
                <Checkbox checked={filter.alertTypes.indexOf(AlertTypeEnum.ApplicationUnhealthy) > -1} />
                <ListItemText primary="Uygulama Sağlıksız" />
              </MenuItem>
              <MenuItem value={AlertTypeEnum.DiskUsage}>
                <Checkbox checked={filter.alertTypes.indexOf(AlertTypeEnum.DiskUsage) > -1} />
                <ListItemText primary="Disk Kullanımı" />
              </MenuItem>
              <MenuItem value={AlertTypeEnum.TaskNotRun}>
                <Checkbox checked={filter.alertTypes.indexOf(AlertTypeEnum.TaskNotRun) > -1} />
                <ListItemText primary="Görev Çalışmadı" />
              </MenuItem>
              <MenuItem value={AlertTypeEnum.CpuUsage}>
                <Checkbox checked={filter.alertTypes.indexOf(AlertTypeEnum.CpuUsage) > -1} />
                <ListItemText primary="CPU Kullanımı" />
              </MenuItem>
              <MenuItem value={AlertTypeEnum.RamUsage}>
                <Checkbox checked={filter.alertTypes.indexOf(AlertTypeEnum.RamUsage) > -1} />
                <ListItemText primary="RAM Kullanımı" />
              </MenuItem>
              <MenuItem value={AlertTypeEnum.EStatement}>
                <Checkbox checked={filter.alertTypes.indexOf(AlertTypeEnum.EStatement) > -1} />
                <ListItemText primary="eStatement" />
              </MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Önem</InputLabel>
            <Select
              value={filter.severity}
              label="Önem"
              onChange={(e) => setFilter((prev) => ({ ...prev, severity: e.target.value as any }))}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value={AlertSeverityEnum.Low}>Düşük</MenuItem>
              <MenuItem value={AlertSeverityEnum.Medium}>Orta</MenuItem>
              <MenuItem value={AlertSeverityEnum.High}>Yüksek</MenuItem>
              <MenuItem value={AlertSeverityEnum.Critical}>Kritik</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Durum</InputLabel>
            <Select
              value={filter.status}
              label="Durum"
              onChange={(e) => setFilter((prev) => ({ ...prev, status: e.target.value as any }))}
            >
              <MenuItem value="all">Tümü</MenuItem>
              <MenuItem value={AlertStatusEnum.New}>Devam Ediyor</MenuItem>
              <MenuItem value={AlertStatusEnum.Resolved}>Çözüldü</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Paper>

      {/* Alert Tablosu */}
      <Paper
        sx={{
          p: 3,
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1), 0px 1px 2px rgba(0, 0, 0, 0.06)',
          ...getRefreshPaperStyles(isRefreshing),
        }}
      >
        <TableContainer sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50] }}>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableSortLabel active={sort.field === 'createdAt'} direction={sort.field === 'createdAt' ? sort.direction : 'desc'} onClick={() => handleSort('createdAt')}>
                    Zaman
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Mesaj</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Tip</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableSortLabel active={sort.field === 'severity'} direction={sort.field === 'severity' ? sort.direction : 'asc'} onClick={() => handleSort('severity')}>
                    Önem
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>
                  <TableSortLabel active={sort.field === 'status'} direction={sort.field === 'status' ? sort.direction : 'asc'} onClick={() => handleSort('status')}>
                    Durum
                  </TableSortLabel>
                </TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Server</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>Uygulama</TableCell>
                <TableCell sx={{ fontWeight: 600, fontSize: '0.8rem', py: 1.5, borderBottom: '2px solid', borderColor: 'divider' }}>İşlemler</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAlerts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      Filtrelere uygun alert bulunamadı.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                filteredAlerts.map((alert, index) => (
                  <TableRow
                    key={alert.id}
                    hover
                    sx={{
                      bgcolor: index % 2 === 1 ? 'action.hover' : 'transparent',
                      '&:hover': { bgcolor: 'action.selected' },
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Typography variant="body2" color="text.secondary">
                        {formatTimeAgoTR(alert.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {formatDateToTR(alert.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {alert.message}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Chip label={alert.alertTypeName} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Chip
                        label={alert.severityName}
                        color={getSeverityColor(alert.severity)}
                        size="small"
                        sx={{ fontWeight: 600 }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      {(() => {
                        const statusIcon = getStatusIcon(alert.status);
                        return (
                          <Chip
                            {...(statusIcon && { icon: statusIcon })}
                            label={alert.statusName}
                            color={getStatusColor(alert.status)}
                            size="small"
                            sx={{ fontWeight: 600 }}
                          />
                        );
                      })()}
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Typography variant="body2" color="text.secondary">
                        {alert.serverName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Typography variant="body2" color="text.secondary">
                        {alert.applicationName || '-'}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ fontSize: '0.8rem', py: 1.25 }}>
                      <Box display="flex" gap={1} alignItems="center">
                        {(() => {
                          // Status değerini güvenli şekilde kontrol et
                          const statusValue = typeof alert.status === 'number' 
                            ? alert.status 
                            : typeof alert.status === 'string' 
                            ? parseInt(alert.status, 10) 
                            : alert.status;
                          
                          const isResolved = statusValue === AlertStatusEnum.Resolved || statusValue === 3;
                          
                          if (isResolved) {
                            // Çözülmüş alert - sadece resolution notes varsa göster
                            return alert.resolutionNotes ? (
                              <IconButton 
                                size="small" 
                                color="primary"
                                onClick={() => {
                                  setSelectedAlertForViewNotes(alert);
                                  setViewNotesDialogOpen(true);
                                }}
                                title="Çözüm açıklamasını görüntüle"
                              >
                                <DescriptionIcon fontSize="small" />
                              </IconButton>
                            ) : null;
                          }
                          
                          // Çözülmemiş alert - Çöz butonu göster
                          return (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              startIcon={<CloseIcon />}
                              onClick={() => handleResolveClick(alert)}
                              sx={{ textTransform: 'none' }}
                            >
                              Çöz
                            </Button>
                          );
                        })()}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={pageSize}
          onRowsPerPageChange={handleChangePageSize}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Sayfa başına kayıt:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `~${to}`}`}
        />
      </Paper>

      {/* Resolve Dialog */}
      <Dialog
        open={resolveDialogOpen}
        onClose={handleResolveCancel}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Alert'i Çöz</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            {selectedAlertForResolve?.message}
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Çözüm Açıklaması (Opsiyonel)"
            fullWidth
            multiline
            rows={4}
            variant="outlined"
            value={resolutionNotes}
            onChange={(e) => setResolutionNotes(e.target.value)}
            placeholder="Alert'in nasıl çözüldüğünü açıklayın..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleResolveCancel}>İptal</Button>
          <Button onClick={handleResolveConfirm} variant="contained" color="success">
            Çöz
          </Button>
        </DialogActions>
      </Dialog>

      {/* Resolution Notes View Dialog */}
      <Dialog
        open={viewNotesDialogOpen}
        onClose={() => {
          setViewNotesDialogOpen(false);
          setSelectedAlertForViewNotes(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Çözüm Açıklaması</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Alert:</strong> {selectedAlertForViewNotes?.message}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              <strong>Çözüldü:</strong> {selectedAlertForViewNotes?.resolvedAt 
                ? formatTimeAgoTR(selectedAlertForViewNotes.resolvedAt) 
                : '-'}
            </Typography>
            {selectedAlertForViewNotes?.resolvedByUserName && (
              <Typography variant="body2" color="text.secondary" gutterBottom>
                <strong>Çözen:</strong> {selectedAlertForViewNotes.resolvedByUserName}
              </Typography>
            )}
          </DialogContentText>
          <Box
            sx={{
              p: 2,
              bgcolor: (theme) => theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50],
              borderRadius: 1,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
              {selectedAlertForViewNotes?.resolutionNotes}
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setViewNotesDialogOpen(false);
              setSelectedAlertForViewNotes(null);
            }}
            variant="contained"
          >
            Kapat
          </Button>
        </DialogActions>
      </Dialog>
      </Box>
    </Layout>
  );
};

export default Alerts;

